/**
 * `AIAnswerAttribution.ts` — the attribution check: which documents state what
 * Spark said. Web-only; Spark Answer References, Phase 1.
 *
 * The references under a document answer used to be every document whose best
 * passage scored at least 0.6 for SIMILARITY to the search. When every
 * document describes the same machine that is nearly all of them: in the
 * 2026-10-06 broaching trace, 15 of the 24 references under 7 answers pointed
 * at a document that stated nothing in the answer. Similarity measures how much
 * a passage resembles the question, never whether it states the answer.
 *
 * So once Spark's answer text has settled, a small model checks every retrieved
 * passage against it and must QUOTE the words that support each statement.
 * Code then verifies each quote against the passage text, and only documents
 * with a verified quote are shown. That is the grounding-check pattern Google,
 * AWS and Azure ship, plus Anthropic's rule that every citation carries text
 * that exists in its source.
 *
 * It never delays Spark: the check needs only the finished TEXT, which the
 * voice model produces well before it finishes SPEAKING, so it runs while the
 * audio plays and only the reference chip waits for it. Measured 2026-10-06 on
 * the trace's 7 answers: 1.9 s median and 2.8 s at the 90th percentile on
 * gpt-5.6-terra, ready before Spark finished speaking in 133 of 133 runs.
 *
 * Headless: no Vue, no DOM. `useSpark` calls `attributeAsync` when an answer
 * settles and attaches the result to that answer's entry.
 *
 * Decisions taken by the owner on 2026-10-06 (design doc §15):
 *  - D1: a document that supports only PART of the answer is shown. The models
 *    are stricter than that by default, so the instructions say it outright.
 *  - D2: when nothing supports a non-refusal answer, show nothing.
 *  - D3: when the check fails or overruns `K_ATTRIBUTION_TIMEOUT_MS`, show the
 *    top-scored document only, since the Answer skill tells Spark to answer
 *    from it.
 *  - D4: the check runs on gpt-5.6-terra.
 *  - D5: no deployment switch; this replaces the similarity list.
 */

import { Emitter } from "@core/events/emitter";
import type { UsageDelta } from "@ai/observability/AISessionUsageTracker";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import { postResponsesAsync, tryParseResponsesUsage } from "@ai/subagents/aiResponsesUtility";

/** D4: the most accurate model measured (18 of 21 runs matched the hand-checked references). */
export const K_ATTRIBUTION_MODEL = "gpt-5.6-terra";

/**
 * §8.3: past this the check is abandoned for the top-document fallback.
 * Nothing waits on it, so this only bounds how long a reference can be late.
 *
 * 5 s, raised from the design's 4 s (user direction, 2026-10-07). On the live
 * broaching run the check's p90 was 3.7 s, and one answer fell back at 4.0 s.
 * A late chip costs nothing, while a fallback replaces the checked documents
 * with the top-scored one.
 */
export const K_ATTRIBUTION_TIMEOUT_MS = 5_000;

/** §8.1 step 4: a shorter "quote" is too easy to find by accident. */
export const K_MIN_QUOTE_WORDS = 4;

/** Distinct answers remembered. A repeat question costs nothing (§8.3). */
const K_ATTRIBUTION_CACHE_MAX = 64;

/**
 * §8.2 with D1, reworded where the design's wording measured short.
 *
 * Measured 2026-10-06 on the trace's 7 answers, 21 real calls each, against
 * the design's hand-checked references (§9):
 *  - The design's wording, D1 sentence included: precision 21/21, recall 21/27.
 *    All six misses were D1's two borderline lines (the Process Control Plan's
 *    "36 TEETH", the Lockout Tagout rule about the ram): the model kept the
 *    answer's sentences whole, so a passage stating half of one did not count.
 *  - "one fact or instruction each" and "list them all" brought recall to 26/27
 *    but let in a statement the answer never made, built from a passage
 *    ("the station can be used to try to start the cell during lockout
 *    verification"), which put the Lockout Tagout Procedure under an answer it
 *    does not support: precision 26/28.
 *  - "Every statement comes from the answer" closed that: precision 25/25,
 *    recall 25/27 (19 of 21 runs exactly the hand-checked set).
 *  - Asking for the SHORTEST quote was tried for speed and rejected: quotes fell
 *    under `K_MIN_QUOTE_WORDS` ("36-TOOTH INTERNAL SPLINE") and recall dropped.
 */
export const K_ATTRIBUTION_INSTRUCTIONS = [
  "You check whether passages from project documents support an answer.",
  "Split the answer into short statements, one fact or instruction each. Every statement comes from the answer: never add one the answer does not make.",
  "Check every passage on its own against every statement, and list every passage that states it, quoting the exact words from the passage. Several passages, and several documents, can state the same statement: list them all.",
  "A passage supports a statement only if it states it. Mentioning the same subject, machine or part is not support.",
  "A passage that states only part of a statement still supports it, including a figure or rule given in a table or drawing note. List it for that statement.",
  "Quote the passage, never the answer. Use only the passages given.",
  "A statement no passage states gets an empty support list.",
].join("\n");

// ---- Inputs and results ----------------------------------------------

/** One retrieved passage, as `Prompt_Search_Docs` published it. */
export interface AttributionPassage {
  readonly fileId: string;
  readonly fileName: string | null;
  readonly displayName: string | null;
  readonly storagePath: string | null;
  readonly score: number | null;
  readonly text: string;
}

export interface AttributionRequest {
  /** The user's words, when known. Context for the checker, never judged. */
  readonly question: string | null;
  /** What Spark searched with, one per search in the turn. */
  readonly searchQueries: ReadonlyArray<string>;
  /** Spark's answer, as it settled. */
  readonly answer: string;
  /** Every passage the turn's searches returned, in the order they came back. */
  readonly passages: ReadonlyArray<AttributionPassage>;
}

export interface AttributionSupport {
  readonly statement: string;
  readonly quote: string;
  /** `P1`… as labelled for the checker. */
  readonly passageId: string;
}

/** A document kept under the answer, with the quotes that earned it. */
export interface AttributedReference {
  readonly fileId: string;
  readonly fileName: string | null;
  readonly displayName: string | null;
  readonly storagePath: string | null;
  /** Its best passage score, used only to order equals. */
  readonly bestScore: number | null;
  readonly supports: ReadonlyArray<AttributionSupport>;
}

export type AttributionFallbackReason = "timeout" | "error" | "invalid_output";

export interface AttributionResult {
  /** `attributed`: the check ran and was verified. `fallback`: D3. */
  readonly outcome: "attributed" | "fallback";
  readonly references: ReadonlyArray<AttributedReference>;
  /** Statements no verified quote supports (BP5: a signal, never covered over). */
  readonly unsupported: ReadonlyArray<string>;
  /** Candidate documents not shown, and why. */
  readonly dropped: ReadonlyArray<{ readonly document: string; readonly reason: string }>;
  /** Support entries rejected by verification. */
  readonly quoteMismatches: ReadonlyArray<{ readonly passageId: string; readonly quote: string; readonly reason: string }>;
  readonly latencyMs: number;
  readonly cached: boolean;
  readonly fallbackReason?: AttributionFallbackReason;
}

/** What the checker returned, before any verification. */
export interface AttributionClaim {
  readonly statement: string;
  readonly support: ReadonlyArray<{ readonly passageId: string; readonly quote: string }>;
}

// ---- Pure steps (exported so they can be driven on their own) ----------

/**
 * Folds the differences PDF text and model output disagree on: case,
 * whitespace and line breaks, curly quotes, the dash family, soft hyphens, and
 * table pipes. A model quoting a table row writes its cells with `|`, which the
 * extracted PDF text never has ("Selector switch OFF / AUTO / ACCESS | Access
 * Door Pushbutton Station" against a passage holding the same words on three
 * lines), so a pipe counts as a space.
 */
export function normalizeForQuote(text: string): string {
  return text
    .normalize("NFKC")
    .replace(/­/g, "")
    .replace(/\|/g, " ")
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** `P1`, `P2`… in the order given. */
export function passageIds(count: number): string[] {
  return Array.from({ length: count }, (_, index) => `P${index + 1}`);
}

function documentName(passage: AttributionPassage): string {
  const title = passage.displayName?.trim();
  const file = passage.fileName?.trim();
  if (title && file && title !== file) return `${title} (file ${file})`;
  return title || file || passage.storagePath || passage.fileId;
}

/** The Responses request: the instructions, the inputs, and a strict schema. */
export function buildAttributionRequestBody(request: AttributionRequest): Record<string, unknown> {
  const ids = passageIds(request.passages.length);
  const lines: string[] = [];
  if (request.question?.trim()) lines.push(`Question: ${request.question.trim()}`);
  for (const query of request.searchQueries) {
    if (query.trim()) lines.push(`Search query: ${query.trim()}`);
  }
  lines.push(`Answer: ${request.answer.trim()}`, "", "Passages:");
  request.passages.forEach((passage, index) => {
    lines.push("", `${ids[index]} (${documentName(passage)}):`, passage.text.trim());
  });

  return {
    model: K_ATTRIBUTION_MODEL,
    instructions: K_ATTRIBUTION_INSTRUCTIONS,
    input: [{ role: "user", content: [{ type: "input_text", text: lines.join("\n") }] }],
    // gpt-5.x accepts "none"; the measured run used it.
    reasoning: { effort: "none" },
    // ⚠ `passage_id` with an ENUM of the ids, not the design's `passage`: a
    // field called `passage` got filled with passage TEXT by small models in
    // the 2026-10-06 benchmark, and every such quote then failed verification.
    text: {
      format: {
        type: "json_schema",
        name: "answer_attribution",
        strict: true,
        schema: {
          type: "object",
          additionalProperties: false,
          required: ["claims"],
          properties: {
            claims: {
              type: "array",
              items: {
                type: "object",
                additionalProperties: false,
                required: ["statement", "support"],
                properties: {
                  statement: { type: "string" },
                  support: {
                    type: "array",
                    items: {
                      type: "object",
                      additionalProperties: false,
                      required: ["passage_id", "quote"],
                      properties: {
                        passage_id: { type: "string", enum: ids },
                        quote: { type: "string" },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    store: false,
  };
}

/** The checker's JSON, or null when it is not the shape the schema promised. */
export function parseAttributionClaims(outputText: string | null): AttributionClaim[] | null {
  if (!outputText?.trim()) return null;
  let root: unknown;
  try {
    root = JSON.parse(outputText);
  } catch {
    return null;
  }
  if (!isRecord(root) || !Array.isArray(root.claims)) return null;
  const claims: AttributionClaim[] = [];
  for (const raw of root.claims) {
    if (!isRecord(raw) || typeof raw.statement !== "string" || !Array.isArray(raw.support)) return null;
    const support: Array<{ passageId: string; quote: string }> = [];
    for (const entry of raw.support) {
      if (!isRecord(entry) || typeof entry.passage_id !== "string" || typeof entry.quote !== "string") return null;
      support.push({ passageId: entry.passage_id, quote: entry.quote });
    }
    claims.push({ statement: raw.statement.trim(), support });
  }
  return claims;
}

/**
 * §8.1 steps 4 and 5: keep a support entry only when its passage exists, its
 * quote is at least `K_MIN_QUOTE_WORDS` words, and the normalized quote is a
 * substring of the normalized passage; then show each document with at least
 * one verified support, most statements supported first, then best score.
 */
export function buildAttributedReferences(
  passages: ReadonlyArray<AttributionPassage>,
  claims: ReadonlyArray<AttributionClaim>,
  latencyMs: number,
): AttributionResult {
  const ids = passageIds(passages.length);
  const byId = new Map(ids.map((id, index) => [id, passages[index]] as const));
  const normalizedText = new Map(ids.map((id, index) => [id, normalizeForQuote(passages[index].text)] as const));

  const documents = new Map<string, {
    readonly passage: AttributionPassage;
    bestScore: number | null;
    readonly supports: AttributionSupport[];
    readonly statements: Set<string>;
  }>();
  const unsupported: string[] = [];
  const quoteMismatches: Array<{ passageId: string; quote: string; reason: string }> = [];

  for (const claim of claims) {
    let verified = 0;
    for (const entry of claim.support) {
      const passage = byId.get(entry.passageId);
      if (!passage) {
        quoteMismatches.push({ passageId: entry.passageId, quote: entry.quote, reason: "unknown_passage" });
        continue;
      }
      const quote = entry.quote.trim();
      if (quote.split(/\s+/).filter(Boolean).length < K_MIN_QUOTE_WORDS) {
        quoteMismatches.push({ passageId: entry.passageId, quote, reason: "too_short" });
        continue;
      }
      if (!normalizedText.get(entry.passageId)!.includes(normalizeForQuote(quote))) {
        quoteMismatches.push({ passageId: entry.passageId, quote, reason: "quote_mismatch" });
        continue;
      }
      verified++;
      const key = documentKey(passage);
      let doc = documents.get(key);
      if (!doc) {
        doc = { passage, bestScore: null, supports: [], statements: new Set() };
        documents.set(key, doc);
      }
      doc.supports.push({ statement: claim.statement, quote, passageId: entry.passageId });
      doc.statements.add(claim.statement);
    }
    if (verified === 0 && claim.statement) unsupported.push(claim.statement);
  }

  // A document's best score is over ALL its passages, so a supporting but
  // lower-ranked passage does not demote a document the search ranked first.
  for (const passage of passages) {
    const doc = documents.get(documentKey(passage));
    if (doc && passage.score != null && (doc.bestScore == null || passage.score > doc.bestScore)) {
      doc.bestScore = passage.score;
    }
  }

  const references = [...documents.values()]
    .sort((a, b) =>
      b.statements.size - a.statements.size
      || (b.bestScore ?? -1) - (a.bestScore ?? -1))
    .map((doc): AttributedReference => ({
      fileId: doc.passage.fileId,
      fileName: doc.passage.fileName,
      displayName: doc.passage.displayName,
      storagePath: doc.passage.storagePath,
      bestScore: doc.bestScore,
      supports: doc.supports,
    }));

  const dropped: Array<{ document: string; reason: string }> = [];
  const seen = new Set<string>();
  for (const passage of passages) {
    const key = documentKey(passage);
    if (documents.has(key) || seen.has(key)) continue;
    seen.add(key);
    dropped.push({ document: documentName(passage), reason: "no_verified_support" });
  }

  return { outcome: "attributed", references, unsupported, dropped, quoteMismatches, latencyMs, cached: false };
}

/** D3: the check failed, so show the top-scored document alone. */
export function buildFallbackReferences(
  passages: ReadonlyArray<AttributionPassage>,
  reason: AttributionFallbackReason,
  latencyMs: number,
): AttributionResult {
  let top: AttributionPassage | null = null;
  for (const passage of passages) {
    if (!top || (passage.score ?? -1) > (top.score ?? -1)) top = passage;
  }
  const references: AttributedReference[] = top
    ? [{
        fileId: top.fileId,
        fileName: top.fileName,
        displayName: top.displayName,
        storagePath: top.storagePath,
        bestScore: top.score,
        supports: [],
      }]
    : [];
  return {
    outcome: "fallback",
    references,
    unsupported: [],
    dropped: [],
    quoteMismatches: [],
    latencyMs,
    cached: false,
    fallbackReason: reason,
  };
}

/** Same answer and same passages → same result (§8.3, R3). */
export function attributionCacheKey(request: AttributionRequest): string {
  const parts = [normalizeForQuote(request.answer)];
  for (const passage of request.passages) parts.push(`${documentKey(passage)}#${hashText(passage.text)}`);
  return parts.join("\n");
}

// ---- The model --------------------------------------------------------

export interface AIAnswerAttributionDeps {
  /** The trace log the `citation.*` notes go to. */
  readonly log?: IRealtimeConversationLog | null;
  /** Where the check's token usage is counted. */
  readonly trackUsage?: (usage: UsageDelta) => void;
  /** Injectable for a harness; defaults to the authenticated proxy. */
  readonly post?: typeof postResponsesAsync;
  readonly now?: () => number;
}

export interface AIAnswerAttributedEvent {
  readonly request: AttributionRequest;
  readonly result: AttributionResult;
}

export class AIAnswerAttributionModel {
  /** Every finished check, for the eval driver and anyone else observing. */
  readonly onAttributed = new Emitter<AIAnswerAttributedEvent>();

  private readonly m_cache = new Map<string, AttributionResult>();
  private readonly m_post: typeof postResponsesAsync;
  private readonly m_now: () => number;

  constructor(private readonly m_deps: AIAnswerAttributionDeps = {}) {
    this.m_post = m_deps.post ?? postResponsesAsync;
    this.m_now = m_deps.now ?? (() => performance.now());
  }

  /**
   * The references for one answer. Never throws: a failure is the D3
   * fallback, and an empty `passages` is an empty result with no call.
   */
  async attributeAsync(request: AttributionRequest, signal?: AbortSignal): Promise<AttributionResult> {
    if (request.passages.length === 0 || !request.answer.trim()) {
      return { outcome: "attributed", references: [], unsupported: [], dropped: [], quoteMismatches: [], latencyMs: 0, cached: false };
    }

    const key = attributionCacheKey(request);
    const hit = this.m_cache.get(key);
    if (hit) {
      // Refresh its place so a session's recent answers stay warm.
      this.m_cache.delete(key);
      this.m_cache.set(key, hit);
      const cached: AttributionResult = { ...hit, cached: true, latencyMs: 0 };
      this.record(request, cached);
      return cached;
    }

    const started = this.m_now();
    let result: AttributionResult;
    try {
      const root = await this.m_post(buildAttributionRequestBody(request), signal, K_ATTRIBUTION_TIMEOUT_MS);
      const usage = tryParseResponsesUsage(root, K_ATTRIBUTION_MODEL);
      if (usage) this.m_deps.trackUsage?.(usage);
      const claims = parseAttributionClaims(extractOutputText(root));
      const latency = Math.round(this.m_now() - started);
      result = claims
        ? buildAttributedReferences(request.passages, claims, latency)
        : buildFallbackReferences(request.passages, "invalid_output", latency);
    } catch {
      const latency = Math.round(this.m_now() - started);
      const reason: AttributionFallbackReason = latency >= K_ATTRIBUTION_TIMEOUT_MS - 50 ? "timeout" : "error";
      result = buildFallbackReferences(request.passages, reason, latency);
    }

    // Only a verified result is reused: a transient failure must not pin the
    // fallback onto every later repeat of the question.
    if (result.outcome === "attributed") {
      this.m_cache.set(key, result);
      if (this.m_cache.size > K_ATTRIBUTION_CACHE_MAX) {
        this.m_cache.delete(this.m_cache.keys().next().value as string);
      }
    }
    this.record(request, result);
    return result;
  }

  /** §8.1 step 7: the `citation.*` trace notes, then the event. */
  private record(request: AttributionRequest, result: AttributionResult): void {
    const log = this.m_deps.log;
    if (log) {
      if (result.outcome === "fallback") {
        log.note(
          "citation.fallback",
          `${result.fallbackReason ?? "error"} after ${result.latencyMs}ms; kept ${result.references[0]?.fileName ?? "nothing"}`,
          { model: K_ATTRIBUTION_MODEL, reason: result.fallbackReason, latencyMs: result.latencyMs, kept: result.references.map(referenceLabel) },
        );
      } else {
        log.note(
          "citation.attributed",
          `kept ${result.references.length}, dropped ${result.dropped.length}, unsupported ${result.unsupported.length}, `
            + `quote_mismatch ${result.quoteMismatches.length}, ${result.cached ? "cached" : `${result.latencyMs}ms`}`,
          {
            model: K_ATTRIBUTION_MODEL,
            latencyMs: result.latencyMs,
            cached: result.cached,
            kept: result.references.map((reference) => ({
              document: referenceLabel(reference),
              supports: reference.supports.map((s) => ({ passage: s.passageId, statement: s.statement, quote: s.quote })),
            })),
            dropped: result.dropped,
            quoteMismatches: result.quoteMismatches,
          },
        );
        if (result.unsupported.length > 0) {
          log.note("citation.unsupported", `${result.unsupported.length} statement(s)`, { statements: result.unsupported });
        }
      }
    }
    this.onAttributed.emit({ request, result });
  }
}

// ---- Helpers ----------------------------------------------------------

/** Same identity `publishCitations` de-duplicates on. */
function documentKey(passage: { readonly storagePath: string | null; readonly fileId: string }): string {
  return (passage.storagePath?.trim() || passage.fileId).toLowerCase();
}

function referenceLabel(reference: AttributedReference): string {
  return reference.displayName || reference.fileName || reference.storagePath || reference.fileId;
}

/** The structured output: the first `output_text` part of the message. */
function extractOutputText(root: Record<string, unknown>): string | null {
  if (typeof root.output_text === "string") return root.output_text;
  const output = Array.isArray(root.output) ? root.output : [];
  for (const item of output) {
    if (!isRecord(item) || item.type !== "message" || !Array.isArray(item.content)) continue;
    for (const part of item.content) {
      if (isRecord(part) && part.type === "output_text" && typeof part.text === "string") return part.text;
    }
  }
  return null;
}

/** A short stable hash (FNV-1a), for the cache key only. */
function hashText(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
